<template>
	<v-card
		variant="outlined"
		width="100%"
		class="sgnl-flex sgnl-flex-column sgnl-justify-space-between"
	>
		<div>
			<div class="card-header">
				<div class="sgnl-hidden sgnl-sm-flex sgnl-flex-shrink-0">
					<img
						:src="userProfileIcon"
						class="user-profile-icon"
					/>
				</div>
				<div class="card-header__info line-clamp-1 sgnl-text-color-default">
					<div class="sgnl-paragraph-m-semibold line-clamp-1">
						<span>{{ authenticatedUser?.attributes?.name ?? authenticatedUser?.attributes?.email }}</span>
						<template v-if="$vuetify.display.smAndUp && authenticatedUser?.attributes?.name">
							({{ authenticatedUser?.attributes?.email }})
						</template>
					</div>
					<div class="sgnl-paragraph-s-regular line-clamp-1 sgnl-text-color-secondary">
						{{ usageCardSubtitle }}
					</div>
				</div>
				<div class="sgnl-flex-shrink-0">
					<TierMarker
						v-if="currentUserTierCode"
						:tierCode="currentUserTierCode"
						:small="true"
					/>
				</div>
			</div>
			<div class="section-divider" />
			<ErrorEmptyState
				v-if="tierUnavailable"
				title="Could not load your Usage Data"
				message="Often this is a temporary network or connectivity issue."
				:compact="true"
				:retrying="retryingTierDetails"
				@retry="reloadTierDetails"
			/>
			<div
				v-else
				class="sgnl-flex sgnl-flex-column sgnl-p-16 sgnl-gap-8 sgnl-paragraph-s-regular sgnl-text-color-secondary"
			>
				Your {{ currentUserTierDetails?.fullTitle }} tier includes:
				<ul
					class="ul-list"
					v-if="currentUserTierDetails"
				>
					<li
						v-for="highlight in allowanceHighlights"
						:key="highlight"
					>
						<Checkmark16 />{{ highlight }}
					</li>
				</ul>
			</div>
		</div>

		<div
			v-if="!tierUnavailable"
			class="float-bottom"
		>
			<div class="section-divider" />
			<div class="dialog-footer">
				<TooltipButton
					v-if="userCanUpgrade"
					btnText="Upgrade"
					variant="flat"
					color="primary"
					:to="{ path: '/billing' }"
				>
					<template #btnContent>
						<span>Upgrade</span>
						<Upgrade16 />
					</template>
				</TooltipButton>
			</div>
		</div>
	</v-card>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Upgrade16 from "@carbon/icons-vue/es/upgrade/16";
import userProfileIcon from "@/assets/icons/user-profile-32.svg";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";
import TierMarker from "@/components/Common/TierMarker.vue";
import TooltipButton from "@/components/Common/TooltipButton.vue";
import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";
import moment, { Moment } from "moment";
import { toStringWithMagnitude, toStringWithByteMagnitude, numberLessThanTenToWords } from "@/js/utilities";
import { mapState } from "pinia";
import { useUserStore } from "@/stores/user";
import { AWSAuthenticatedUserObject } from "@/types/user";

type ComponentData = {
	authenticatedUser: undefined | null | AWSAuthenticatedUserObject;
	retryingTierDetails: boolean;
};

type AllowanceLimit = number | null | undefined;

export default defineComponent({
	name: "UserUsageAllowanceCard",
	components: { TierMarker, TooltipButton, ErrorEmptyState, Upgrade16, Checkmark16 },
	props: {},
	data: (): ComponentData => ({
		authenticatedUser: undefined,
		retryingTierDetails: false,
	}),
	computed: {
		usageResetDay(): undefined | Moment {
			if (!this.currentUserResourceUsage) {
				return undefined;
			}
			return moment(this.currentUserResourceUsage?.ResetsAt * 1000);
		},
		usageResetDayString(): string {
			return this.usageResetDay?.isValid() ? this.usageResetDay.fromNow() : "";
		},
		usageResetDate(): string {
			return this.usageResetDay?.isValid() ? this.usageResetDay.format("MMM D") : "";
		},
		usageCardSubtitle(): string {
			if (this.usageResetDay && this.usageResetDay.isValid() && this.usageResetDay?.isSameOrAfter(moment())) {
				return `Usage quota resets ${this.usageResetDay.fromNow()} on ${this.usageResetDate}.`;
			} else {
				return "Upgrade to increase your monthly quota.";
			}
		},
		...mapState(useUserStore, {
			user: "currentUser",
			currentUserResourceUsage: "currentUserResourceUsage",
			currentUserTierDetails: "currentUserTierDetails",
			currentUserTierCode: "currentUserTierCode",
			tierResolutionFailed: "tierResolutionFailed",
		}),
		tierUnavailable(): boolean {
			return this.tierResolutionFailed;
		},
		allowanceHighlights(): string[] {
			const allowance = this.currentUserTierDetails?.allowance;
			if (!allowance) {
				return [];
			}

			const count = (limit: AllowanceLimit): string => this.toStringWithMagnitude(limit);
			const bytes = (limit: AllowanceLimit): string => this.toStringWithByteMagnitude(limit);
			const plural = (limit: AllowanceLimit): string => (limit === 1 ? "" : "s");

			const {
				DynamicInstructionCount,
				CloudStorageBytes,
				CoreCount,
				PlotCount,
				RepositoryCount,
				DataDriveCount,
				GatewayCount,
				BucketCount,
				ConcurrentTaskCount,
			} = allowance;
			const repositoryNoun = RepositoryCount === 1 ? "repository" : "repositories";
			const concurrentTasksWorthListing = ConcurrentTaskCount === 1 ? null : ConcurrentTaskCount;

			const highlights: [AllowanceLimit, string][] = [
				[
					DynamicInstructionCount,
					`${count(DynamicInstructionCount)} Signaloid C0 Processor Dynamic Instructions per month`,
				],
				[CloudStorageBytes, `${bytes(CloudStorageBytes)} Signaloid Cloud Storage`],
				[CoreCount, `${count(CoreCount)} Signaloid custom C0 processor core${plural(CoreCount)}`],
				[PlotCount, `${count(PlotCount)} Signaloid Distribution Plots`],
				[RepositoryCount, `Connect ${count(RepositoryCount)} ${repositoryNoun}`],
				[DataDriveCount, `${count(DataDriveCount)} Signaloid Data Drive${plural(DataDriveCount)}`],
				[GatewayCount, `${count(GatewayCount)} Signaloid Sensor Gateway connection${plural(GatewayCount)}`],
				[BucketCount, `${count(BucketCount)} AWS S3 Bucket connection${plural(BucketCount)}`],
				[
					concurrentTasksWorthListing,
					`${count(ConcurrentTaskCount)} Concurrent Code Execution Task${plural(ConcurrentTaskCount)}`,
				],
			];

			return highlights.filter(([limit]) => limit !== null).map(([, highlight]) => highlight);
		},
		userCanUpgrade(): boolean {
			return this.currentUserTierCode != "enterprise-tier-l";
		},
	},
	setup() {
		// Note: gets called after beforeCreate in vue2
		const userStore = useUserStore();
		return { userStore, userProfileIcon };
	},
	methods: {
		toStringWithMagnitude(value) {
			if (value === undefined) {
				return "Unlimited";
			}
			if (value !== null && value < 10) {
				const word = numberLessThanTenToWords(value);
				// Make first char uppercase
				return word[0].toUpperCase() + word.slice(1);
			}
			return toStringWithMagnitude(value);
		},
		toStringWithByteMagnitude(value) {
			if (value === undefined) {
				return "Unlimited";
			}
			return toStringWithByteMagnitude(value);
		},
		async reloadTierDetails() {
			this.retryingTierDetails = true;
			try {
				await this.userStore.getCurrentUserTierDetails();
			} finally {
				this.retryingTierDetails = false;
			}
		},
	},
	async created() {
		this.authenticatedUser = await this.userStore.getCurrentAuthenticatedUserObject();
		this.userStore.getCurrentUserTierDetails();
	},
});
</script>

<style lang="scss" scoped>
.float-bottom {
	position: relative;
	top: 0%;
}
</style>
